import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Save, Loader2, Plus, Trash2, RotateCcw } from "lucide-react";
import AdminNav from "@/components/AdminNav";
import { DEFAULT_SITE } from "@/hooks/useSiteSettings";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "od_admin_token";

const inputCls =
  "w-full bg-transparent border border-[#262626] focus:border-[#D6B98D] focus:ring-1 focus:ring-[#D6B98D]/40 rounded-sm px-4 py-3 text-sm text-white placeholder-white/30 outline-none transition-colors";

const labelCls = "block text-[10px] uppercase tracking-[0.3em] text-[#D6B98D] mb-2";

export default function AdminSettings() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [data, setData] = useState(DEFAULT_SITE);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null); // section name being saved

  const auth = { headers: { Authorization: `Bearer ${token}` } };

  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        const { data: d } = await axios.get(`${API}/site`);
        setData({ ...DEFAULT_SITE, ...d });
      } catch {
        toast.error("Failed to load settings");
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  if (!token) return <Navigate to="/admin" replace />;

  const handleLogout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
  };

  const saveSection = async (section, payload) => {
    setSaving(section);
    try {
      const { data: updated } = await axios.put(
        `${API}/site`,
        { [section]: payload },
        auth
      );
      setData({ ...DEFAULT_SITE, ...updated });
      toast.success(`${section.charAt(0).toUpperCase() + section.slice(1)} saved`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(null);
    }
  };

  const resetSection = (section) => {
    if (!window.confirm(`Reset the ${section} section to its defaults?`)) return;
    saveSection(section, DEFAULT_SITE[section]);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] text-white">
        <AdminNav onLogout={handleLogout} />
        <div className="py-32 flex items-center justify-center text-white/50">
          <Loader2 className="w-6 h-6 animate-spin mr-3" /> Loading site settings…
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <AdminNav onLogout={handleLogout} />

      <main className="max-w-5xl mx-auto px-6 lg:px-12 py-12 space-y-12">
        <header>
          <div className="text-xs uppercase tracking-[0.3em] text-[#D6B98D] mb-3">
            Site copy
          </div>
          <h1 className="font-serif text-4xl text-white font-light tracking-tight">
            Edit your homepage
          </h1>
          <p className="mt-3 text-sm text-white/50 max-w-2xl">
            Update everything that appears on the public site — Hero, Packages,
            About, Contact, and Footer. Each section saves independently.
          </p>
        </header>

        <HeroSection
          value={data.hero}
          onChange={(v) => setData({ ...data, hero: v })}
          onSave={(v) => saveSection("hero", v)}
          onReset={() => resetSection("hero")}
          saving={saving === "hero"}
        />

        <PackagesSection
          value={data.packages}
          onChange={(v) => setData({ ...data, packages: v })}
          onSave={(v) => saveSection("packages", v)}
          onReset={() => resetSection("packages")}
          saving={saving === "packages"}
        />

        <AboutSection
          value={data.about}
          onChange={(v) => setData({ ...data, about: v })}
          onSave={(v) => saveSection("about", v)}
          onReset={() => resetSection("about")}
          saving={saving === "about"}
        />

        <ContactSection
          value={data.contact}
          onChange={(v) => setData({ ...data, contact: v })}
          onSave={(v) => saveSection("contact", v)}
          onReset={() => resetSection("contact")}
          saving={saving === "contact"}
        />

        <FooterSection
          value={data.footer}
          onChange={(v) => setData({ ...data, footer: v })}
          onSave={(v) => saveSection("footer", v)}
          onReset={() => resetSection("footer")}
          saving={saving === "footer"}
        />
      </main>
    </div>
  );
}

/* ---------- Section primitives ---------- */
function SectionShell({ id, title, subtitle, children, onSave, onReset, saving }) {
  return (
    <section
      id={`section-${id}`}
      data-testid={`section-${id}`}
      className="p-8 lg:p-10 bg-[#111] border border-[#262626] rounded-sm"
    >
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#D6B98D] mb-2">
            {subtitle}
          </div>
          <h2 className="font-serif text-2xl text-white font-light tracking-tight">
            {title}
          </h2>
        </div>
        <div className="flex gap-2 shrink-0">
          <button
            onClick={onReset}
            data-testid={`reset-${id}`}
            className="inline-flex items-center gap-1.5 px-3 py-2 border border-[#262626] hover:border-white/40 text-[10px] uppercase tracking-[0.25em] text-white/60 rounded-sm transition-all"
            title="Reset to defaults"
          >
            <RotateCcw className="w-3 h-3" />
          </button>
          <button
            onClick={onSave}
            disabled={saving}
            data-testid={`save-${id}`}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#D6B98D] text-[#050505] hover:bg-[#E5CCA5] disabled:opacity-60 text-[10px] uppercase tracking-[0.25em] font-bold rounded-sm transition-all"
          >
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
            Save
          </button>
        </div>
      </div>
      {children}
    </section>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className={labelCls}>{label}</label>
      {children}
    </div>
  );
}

/* ---------- Hero ---------- */
function HeroSection({ value, onChange, onSave, onReset, saving }) {
  const update = (k) => (e) => onChange({ ...value, [k]: e.target.value });
  return (
    <SectionShell
      id="hero"
      subtitle="Section 1"
      title="Hero (top of page)"
      onSave={() => onSave(value)}
      onReset={onReset}
      saving={saving}
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <Field label="Location label (small text above headline)">
          <input className={inputCls} value={value.location_label || ""} onChange={update("location_label")} data-testid="hero-location-label" />
        </Field>
        <Field label="Est. / footer tag (bottom of hero)">
          <input className={inputCls} value={value.tagline_footer || ""} onChange={update("tagline_footer")} data-testid="hero-tagline-footer" />
        </Field>
        <Field label="Headline · line 1">
          <input className={inputCls} value={value.headline_line1 || ""} onChange={update("headline_line1")} data-testid="hero-headline-line1" />
        </Field>
        <Field label="Headline · line 2 (italic, tan)">
          <input className={inputCls} value={value.headline_line2 || ""} onChange={update("headline_line2")} data-testid="hero-headline-line2" />
        </Field>
      </div>
      <div className="mt-5">
        <Field label="Subtitle / paragraph under headline">
          <textarea rows={3} className={`${inputCls} resize-none`} value={value.subtitle || ""} onChange={update("subtitle")} data-testid="hero-subtitle" />
        </Field>
      </div>
    </SectionShell>
  );
}

/* ---------- Packages ---------- */
function PackagesSection({ value, onChange, onSave, onReset, saving }) {
  const updatePkg = (idx, k, v) => {
    const next = value.map((p, i) => (i === idx ? { ...p, [k]: v } : p));
    onChange(next);
  };
  const updateFeature = (idx, fIdx, v) => {
    const pkg = value[idx];
    const feats = [...(pkg.features || [])];
    feats[fIdx] = v;
    updatePkg(idx, "features", feats);
  };
  const addFeature = (idx) => {
    const pkg = value[idx];
    updatePkg(idx, "features", [...(pkg.features || []), ""]);
  };
  const removeFeature = (idx, fIdx) => {
    const feats = (value[idx].features || []).filter((_, i) => i !== fIdx);
    updatePkg(idx, "features", feats);
  };

  return (
    <SectionShell
      id="packages"
      subtitle="Section 2"
      title="Packages (prices & features)"
      onSave={() => onSave(value)}
      onReset={onReset}
      saving={saving}
    >
      <p className="text-xs text-white/40 mb-6">
        These prices are charged at checkout — Stripe pulls from here.
      </p>
      <div className="space-y-8">
        {value.map((pkg, idx) => (
          <div
            key={pkg.id}
            data-testid={`pkg-editor-${pkg.id}`}
            className="p-5 bg-[#0a0a0a] border border-[#262626] rounded-sm"
          >
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#D6B98D] mb-3">
              {pkg.id} package
            </div>
            <div className="grid grid-cols-1 md:grid-cols-12 gap-4 mb-5">
              <div className="md:col-span-5">
                <Field label="Display name">
                  <input className={inputCls} value={pkg.name || ""} onChange={(e) => updatePkg(idx, "name", e.target.value)} data-testid={`pkg-name-${pkg.id}`} />
                </Field>
              </div>
              <div className="md:col-span-4">
                <Field label="Tagline (small overline)">
                  <input className={inputCls} value={pkg.tagline || ""} onChange={(e) => updatePkg(idx, "tagline", e.target.value)} data-testid={`pkg-tagline-${pkg.id}`} />
                </Field>
              </div>
              <div className="md:col-span-3">
                <Field label="Price (USD)">
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    className={inputCls}
                    value={pkg.price ?? 0}
                    onChange={(e) => updatePkg(idx, "price", parseFloat(e.target.value) || 0)}
                    data-testid={`pkg-price-${pkg.id}`}
                  />
                </Field>
              </div>
            </div>

            <div className={labelCls}>Features (one per line)</div>
            <div className="space-y-2">
              {(pkg.features || []).map((feat, fIdx) => (
                <div key={fIdx} className="flex gap-2">
                  <input
                    value={feat}
                    onChange={(e) => updateFeature(idx, fIdx, e.target.value)}
                    className={inputCls}
                    data-testid={`pkg-feature-${pkg.id}-${fIdx}`}
                  />
                  <button
                    onClick={() => removeFeature(idx, fIdx)}
                    data-testid={`pkg-feature-remove-${pkg.id}-${fIdx}`}
                    className="px-3 text-red-400/70 hover:text-red-400 transition-colors"
                    title="Remove feature"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
              <button
                onClick={() => addFeature(idx)}
                data-testid={`pkg-feature-add-${pkg.id}`}
                className="inline-flex items-center gap-2 mt-2 px-3 py-2 border border-dashed border-[#262626] hover:border-[#D6B98D] text-xs text-white/60 hover:text-[#D6B98D] rounded-sm transition-all"
              >
                <Plus className="w-3.5 h-3.5" /> Add feature
              </button>
            </div>
          </div>
        ))}
      </div>
    </SectionShell>
  );
}

/* ---------- About ---------- */
function AboutSection({ value, onChange, onSave, onReset, saving }) {
  const update = (k) => (e) => onChange({ ...value, [k]: e.target.value });
  const updateStat = (idx, k, v) => {
    const next = (value.stats || []).map((s, i) => (i === idx ? { ...s, [k]: v } : s));
    onChange({ ...value, stats: next });
  };
  return (
    <SectionShell
      id="about"
      subtitle="Section 3"
      title="About"
      onSave={() => onSave(value)}
      onReset={onReset}
      saving={saving}
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <Field label="Title · line 1"><input className={inputCls} value={value.title_line1 || ""} onChange={update("title_line1")} data-testid="about-title-line1" /></Field>
        <Field label="Title · line 2"><input className={inputCls} value={value.title_line2 || ""} onChange={update("title_line2")} data-testid="about-title-line2" /></Field>
      </div>
      <div className="mt-5 space-y-5">
        <Field label="Paragraph 1"><textarea rows={3} className={`${inputCls} resize-none`} value={value.paragraph1 || ""} onChange={update("paragraph1")} data-testid="about-paragraph1" /></Field>
        <Field label="Paragraph 2"><textarea rows={3} className={`${inputCls} resize-none`} value={value.paragraph2 || ""} onChange={update("paragraph2")} data-testid="about-paragraph2" /></Field>
      </div>

      <div className="mt-8">
        <div className={labelCls}>Stats (4 boxes)</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {(value.stats || []).map((s, i) => (
            <div key={i} className="grid grid-cols-3 gap-3 p-4 bg-[#0a0a0a] border border-[#262626] rounded-sm">
              <input className={inputCls} value={s.number} onChange={(e) => updateStat(i, "number", e.target.value)} data-testid={`about-stat-number-${i}`} placeholder="500+" />
              <input className={`${inputCls} col-span-2`} value={s.label} onChange={(e) => updateStat(i, "label", e.target.value)} data-testid={`about-stat-label-${i}`} placeholder="Vehicles detailed" />
            </div>
          ))}
        </div>
      </div>
    </SectionShell>
  );
}

/* ---------- Contact ---------- */
function ContactSection({ value, onChange, onSave, onReset, saving }) {
  const update = (k) => (e) => onChange({ ...value, [k]: e.target.value });
  const fields = [
    { key: "phone", label: "Phone", note: "phone_note" },
    { key: "email", label: "Email", note: "email_note" },
    { key: "address", label: "Address / service area", note: "address_note" },
    { key: "hours", label: "Hours", note: "hours_note" },
  ];
  return (
    <SectionShell
      id="contact"
      subtitle="Section 4"
      title="Contact"
      onSave={() => onSave(value)}
      onReset={onReset}
      saving={saving}
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {fields.map((f) => (
          <div key={f.key} className="space-y-3 p-4 bg-[#0a0a0a] border border-[#262626] rounded-sm">
            <Field label={f.label}>
              <input className={inputCls} value={value[f.key] || ""} onChange={update(f.key)} data-testid={`contact-${f.key}`} />
            </Field>
            <Field label="Small note under it">
              <input className={inputCls} value={value[f.note] || ""} onChange={update(f.note)} data-testid={`contact-${f.note}`} />
            </Field>
          </div>
        ))}
      </div>
    </SectionShell>
  );
}

/* ---------- Footer ---------- */
function FooterSection({ value, onChange, onSave, onReset, saving }) {
  const update = (k) => (e) => onChange({ ...value, [k]: e.target.value });
  return (
    <SectionShell
      id="footer"
      subtitle="Section 5"
      title="Footer"
      onSave={() => onSave(value)}
      onReset={onReset}
      saving={saving}
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <Field label="Description (left side)">
          <input className={inputCls} value={value.description || ""} onChange={update("description")} data-testid="footer-description" />
        </Field>
        <Field label="Copyright business name">
          <input className={inputCls} value={value.copyright_name || ""} onChange={update("copyright_name")} data-testid="footer-copyright-name" />
        </Field>
      </div>
    </SectionShell>
  );
}
